import { useEffect } from 'react';

/**
 * SEO component for the homepage
 * Adds meta tags and structured data for better search engine optimization
 */
export function HomeSEO() {
  useEffect(() => {
    // Set document title
    document.title = "VEDAVIVI - Automate Your Day, Reclaim Your Time with AI";

    // Create or update meta tags
    const metaTags = [
      { name: 'description', content: 'Save 2+ hours daily with AI that handles email responses, schedules meetings, and manages tasks automatically. Join 10,000+ users who\'ve reclaimed their time with VEDAVIVI.' },
      { name: 'keywords', content: 'AI personal assistant, time management, task automation, productivity, VEDAVIVI, time calculator, daily tasks' },
      { name: 'author', content: 'VEDAVIVI' },
      { name: 'robots', content: 'index, follow' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      
      // Open Graph tags
      { property: 'og:title', content: 'VEDAVIVI - Reclaim Your Time with AI Personal Assistant' },
      { property: 'og:description', content: 'Discover how much time you could save with VEDAVIVI\'s intelligent automation. Calculate your potential time savings from 800+ daily activities.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href },
      { property: 'og:site_name', content: 'VEDAVIVI' },
      { property: 'og:locale', content: 'en_US' },
      
      // Twitter Card tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'VEDAVIVI - Automate Your Day, Reclaim Your Time with AI' },
      { name: 'twitter:description', content: 'Save 2+ hours daily with AI that handles email responses, schedules meetings, and manages tasks automatically. Join 10,000+ users.' },
      
      // Additional SEO tags
      { name: 'theme-color', content: '#2dd4bf' },
      { name: 'msapplication-TileColor', content: '#2dd4bf' },
    ];

    // Apply meta tags
    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (name) meta.name = name;
        if (property) meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      
      meta.content = content;
    });

    // Add structured data (JSON-LD)
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "VEDAVIVI",
      "description": "AI personal assistant that automates daily tasks and helps you reclaim your time",
      "url": window.location.origin,
      "applicationCategory": "ProductivityApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Free to get started"
      },
      "featureList": [
        "Email management and prioritization",
        "Calendar scheduling and optimization",
        "Task automation",
        "Time tracking and analytics",
        "Personal productivity insights"
      ],
      "screenshot": `${window.location.origin  }/og-image.png`,
      "author": {
        "@type": "Organization",
        "name": "VEDAVIVI",
        "url": window.location.origin
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "150",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // Add or update structured data script
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.type = 'application/ld+json';
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = JSON.stringify(structuredData);

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const [baseUrl] = window.location.href.split('?'); // Remove query parameters
    canonical.href = baseUrl;

    // Cleanup function
    return () => {
      // Reset title when component unmounts
      document.title = 'VEDAVIVI';
    };
  }, []);

  return null; // This component doesn't render anything
}

export default HomeSEO;
